import { useState } from 'react';
import { Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Dropdown, TextField, Spinner, SpinnerSize, MessageBar, MessageBarType } from '@fluentui/react';
import { LIFESPAN_OPTIONS } from '../../types/environment';
import { claimDevBox } from '../../services/environments';

interface ClaimDevBoxDialogProps {
  workspaceCode: string;
  userEmail: string;
  onDismiss: () => void;
  onClaimed: () => void;
}

export default function ClaimDevBoxDialog({ workspaceCode, userEmail, onDismiss, onClaimed }: ClaimDevBoxDialogProps) {
  const [lifespan, setLifespan] = useState('7');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  async function handleClaim() {
    setSubmitting(true);
    setError('');
    try {
      await claimDevBox({
        upn: userEmail,
        projectCode: workspaceCode,
        lifespan,
        description: description.trim() || `AgentBox DevBox for ${workspaceCode}`,
      });
      onClaimed();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to claim DevBox');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      hidden={false}
      onDismiss={onDismiss}
      dialogContentProps={{
        type: DialogType.normal,
        title: 'Claim DevBox',
        subText: `Claim a new DevBox environment for workspace ${workspaceCode}.`,
      }}
      modalProps={{ isBlocking: submitting }}
    >
      {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={() => setError('')}>{error}</MessageBar>}

      <Dropdown
        label="Lifespan"
        options={LIFESPAN_OPTIONS.map((o) => ({ key: o.key, text: o.text }))}
        selectedKey={lifespan}
        onChange={(_, opt) => opt && setLifespan(opt.key as string)}
      />

      <TextField
        label="Description (optional)"
        placeholder="What will you use this environment for?"
        value={description}
        onChange={(_, v) => setDescription(v || '')}
        multiline
        rows={2}
      />

      <DialogFooter>
        <PrimaryButton
          text={submitting ? 'Claiming…' : 'Claim DevBox'}
          onClick={handleClaim}
          disabled={submitting}
          onRenderIcon={submitting ? () => <Spinner size={SpinnerSize.xSmall} /> : undefined}
        />
        <DefaultButton text="Cancel" onClick={onDismiss} disabled={submitting} />
      </DialogFooter>
    </Dialog>
  );
}
